import { memo } from 'react';
import { Crown, List } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Move } from '@draughts/shared';
import { formatMoveNotation } from '@draughts/core';
import { useGameStore } from '../../store/game-store';
import { colorLeading } from '../../utils/challenge-icons';
import { moverOf } from '../../hooks/useBlindMoveAnnouncement';
import './BlindLastMoveButton.css';

/** Riferimento stabile: un `[]` nuovo a ogni selettore rifarebbe il render a vuoto. */
const EMPTY_MOVES: Move[] = [];

/**
 * Oltre questa lunghezza la notazione si RIMPICCIOLISCE invece di farsi tagliare.
 *
 * La soglia sta sui caratteri e non sui pixel perche' il font e' monospazio: la
 * larghezza e' funzione dei glifi, quindi il conto e' esatto e si puo' verificare in
 * jsdom, che non fa layout. Tredici e' quel che entra a 360px accanto alla Chat.
 */
const LONG_NOTATION_CHARS = 13;

interface BlindLastMoveButtonProps {
  /** Apre la lista mosse completa (il pannello a schermo intero). */
  onOpen: () => void;
}

/**
 * «12. 33-28» nella barra in-game, al posto delle frecce di revisione.
 *
 * Alla cieca quelle frecce non hanno niente da mostrare — sotto la benda la posizione
 * rivista e' vuota quanto quella corrente — e in una partita online fanno pure danno:
 * entrare in revisione blocca la damiera. Nel posto che liberano va l'unica cosa che
 * bendati conta davvero, scritta grande: che mossa e' stata giocata, e da chi.
 *
 * Non e' un doppione dell'annuncio nella scheda del giocatore: quello dura cinque
 * secondi ed e' la voce dell'arbitro, questo resta li' ed e' il referto. Per la stessa
 * ragione qui NON c'e' `role="status"`: la regione viva e' gia' quella, e una seconda
 * farebbe leggere ogni mossa due volte proprio a chi usa uno screen reader.
 *
 * Legge lo store di gioco e non le prop dello schermo, come `useBlindMoveAnnouncement`:
 * l'online riversa la board del server dentro questo store con `syncFromExternal`, che
 * scrive `moveHistory`, `variant` e `currentTurn`. Vale quindi anche per arena e torneo.
 */
export const BlindLastMoveButton = memo(function BlindLastMoveButton({ onOpen }: BlindLastMoveButtonProps) {
  const { t } = useTranslation();
  const moveHistory = useGameStore((s) => s.moveHistory ?? EMPTY_MOVES);
  const variant = useGameStore((s) => s.variant);
  const currentTurn = useGameStore((s) => s.currentTurn);

  const move = moveHistory[moveHistory.length - 1] ?? null;
  const notation = move ? formatMoveNotation(move, variant) : '';
  const long = notation.length > LONG_NOTATION_CHARS;

  return (
    <button
      type="button"
      className={`gs-mobile-bar-btn blind-last-move${long ? ' blind-last-move--long' : ''}`}
      data-testid="blind-last-move"
      onClick={onOpen}
      /* Col corpo ridotto la notazione ci sta comunque; il `title` e' la rete per il
         caso estremo in cui nemmeno quello basta e l'ellissi entra in scena. */
      title={move ? `${t('game.blind.lastMove')}: ${notation}` : t('game.sidebar.moves')}
    >
      {move ? (
        <>
          {/* Fuori dalla vista ma non dal significato: uno screen reader che legge
              «33-28» e basta non ha detto niente. Stesso trucco del banner. */}
          <span className="blind-last-move__label">{t('game.blind.lastMove')}: </span>
          <span className="blind-last-move__dot" aria-hidden="true">
            {colorLeading(moverOf(currentTurn), 16)}
          </span>
          <span className="blind-last-move__num">{Math.ceil(moveHistory.length / 2)}.</span>
          <span className="blind-last-move__notation">{notation}</span>
          {/* Che l'avversario abbia appena fatto dama e' l'informazione che pesa di
              piu', e la notazione da sola non la dice. */}
          {move.isPromotion && (
            <Crown className="blind-last-move__crown" size={16} aria-label={t('game.blind.promoted')} />
          )}
        </>
      ) : (
        <span className="blind-last-move__empty">{t('game.sidebar.noMoves', 'No moves yet')}</span>
      )}
      {/* Il blocco resta premibile anche prima della prima mossa: e' l'unica porta per
          la lista completa, che qui ha preso il posto del bottone «Mosse». */}
      <List className="blind-last-move__open" size={16} aria-hidden="true" />
      <span className="blind-last-move__sr">{t('game.sidebar.moves', 'Moves')}</span>
    </button>
  );
});

export default BlindLastMoveButton;
